/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   Wed Mar 8 2023 3:16:54 PM
 *
 *******************************************************/
//@flow

import _ from 'lodash';
import React, { Component } from 'react';
import { Page, Layout } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { connect } from 'react-redux';
import moment from 'moment';

import MainMenu from 'src/components/containers/main-menu';
import CampaignCarousel from './campaignCarousel';
import CampaignBanner from './campaignBanner';
import MarketingCampaignBanner from './marketingCampaignBanner';
import WelcomeCustom from './welcome3';
import Notifications from './notifications/index';
import BannerInstallStatus from './bannerInstallStatus/index';
import QuickStartGuideV2 from './quickStartGuideV2';
import ThemeSetupStatus from './themeSetupStatus';
import OptionInEmail from './optionInEmail';
import SyncHookStatus from './syncHookStatus';
import ChangeLogs from './changeLogs';
import RecommendedApps from './recommendedApps';
import { APP_LANGUAGE_ENGLISH_VALUE } from 'src/constants/language-setting';
import * as dashboardV3Action from 'actions/dashboard-v3-actions';
import * as quickStartGuideAction from 'actions/dashboard/quick-start-guide-action';
import * as TranslationActions from 'actions/translation-actions';
import * as subscriptionV3Actions from 'src/stores/actions/subscription-v3-actions';
import { getShop } from 'src/helpers';
import Emitter, { EVENT_KEY } from 'src/libs/eventEmitter';

import {
  getDashboardV4DisplayBlocksColumn,
  checkShowPersonalizationCampaignBanner
} from 'src/helpers/dashboard-helper';
import { DASHBOARD_V4_POSITION } from 'src/constants/dashboard-v4';
import { allowToShowBFCM23 } from 'src/helpers/dashboard-helper';
import { isFreemiumPlan } from 'src/helpers/subscription-v4-helper';
import YearEndReviewPopup from 'src/components/containers/common/year-end-review-popup';
import GermanyTestSearchEngineSurvey from 'src/components/containers/common/germany-test-search-engine-survey';
import LuckyDrawModal from 'src/components/views/lucky-draw/lucky-draw-modal';
import AISearchCampaign0424Popup from 'src/components/containers/common/AI-search-campaign-0424-popup';
import AppInsight from '../analytics-v2/app-insight';
import PlanSubscription from './planSubscription';
import { enableShopifyIntegration } from 'src/helpers/theme-setup-helper';
import BoostInsightHub from './boostInsightHub';
import HelpAndSupport from './helpSupport';
import ResourceCenter from './resourceCenter';
import BannerUpgradeTurbo, {
  KEY_BOOST_TURBO_BANNER_DASHBOARD,
  KEY_TO_TURBO_BANNER_DASHBOARD
} from '../dashboard-v3/banner-upgrade-turbo';
import BoostTurboModal from 'src/components/containers/common/boost-turbo-modal';
import { APP_VERSION_OLD_VERSION, APP_VERSION_TURBO } from 'src/constants/accounts';
import { isInWhiteListTurbo, isInWhiteListTurboV2 } from 'src/helpers/whitelist';
import { checkUserEnableShowAppVersionSwitching } from 'src/helpers/account-helper';

type Props = {
  dashboardBlocks: Array<string>,
  enableAISearch: boolean,
  getDashboardBlocks: () => void,
  getCampaignBanners: () => void,
  getDashboardComponents: (language: string) => void,
  getLocales: () => void
};
type ComposedProps = Props & WithI18nProps;
/**
 * Render associated component based on name
 *
 * @param  {string} name component name
 *
 * @return {object}      react component
 */
const renderComponent = (name, option = {}) => {
  const { type = '', handleDismissBanner } = option;
  const components = {
    welcome3: <WelcomeCustom blockName="welcome" />,
    marketingCampaignBanner: (
      <MarketingCampaignBanner type={type} handleDismissBanner={handleDismissBanner} />
    ),
    marketingBanner: <CampaignCarousel />,
    notifications: <Notifications blockName="notifications" />,
    appInsight: <AppInsight blockName="appInsight" />,
    quickStartGuide: <QuickStartGuideV2 blockName="quickStartGuideV2" />,
    planSubscription: <PlanSubscription blockName="planSubscription" />,
    themeSetupStatus: <ThemeSetupStatus blockName="themeSetupStatus" />,
    getSupport: (
      <div style={{ gap: '1.5rem', flexDirection: 'column', display: 'flex' }}>
        <HelpAndSupport />
        <ResourceCenter />
      </div>
    ),
    optionInEmail: <OptionInEmail blockName="optionInEmail" />,
    syncHookStatus: <SyncHookStatus blockName="syncHookStatus" />,
    changeLogs: <ChangeLogs blockName="changeLogs" />,
    recommendedApps: <RecommendedApps blockName="recommendedApps" />,
    marketingMiddleBanner: <CampaignBanner />,
    boostInsightHub: <BoostInsightHub />,
    upgradeTurbo: <BannerUpgradeTurbo isDashboard />
  };

  return components[name] || '';
};

class DashboardV3 extends Component<ComposedProps> {
  state = {
    activeBFCMPopup: false,
    showCampaignBannerType: ''
  };

  componentDidMount() {
    const {
      language,
      tenantApp,
      locales,
      getDashboardBlocks,
      getDashboardComponents,
      getCampaignBanners,
      getLocales,
      getDetailInvoice
    } = this.props;

    if (locales.length === 0) getLocales();
    // Render shopify menu bar
    // buttonTitleBarSAB('Dashboard');

    // Get list of blocks to be displayed
    getDetailInvoice();
    getDashboardBlocks();
    getDashboardComponents(language);
    getCampaignBanners();

    // Show popup BFCM23
    setTimeout(() => {
      const isAllowSpecialDiscount = allowToShowBFCM23(tenantApp);

      try {
        let countShowPopup = localStorage.getItem(`${tenantApp?.tenantId}-countShowPopupBfcm`) || 0;
        const countShowPopupDate = localStorage.getItem(
          `${tenantApp?.tenantId}-countShowPopupBfcmDate`
        );

        if (!countShowPopupDate) {
          localStorage.setItem(
            `${tenantApp?.tenantId}-countShowPopupBfcmDate`,
            moment().endOf('day').toISOString()
          );
        } else {
          if (countShowPopupDate !== moment().endOf('day').toISOString()) {
            localStorage.setItem(
              `${tenantApp?.tenantId}-countShowPopupBfcmDate`,
              moment().endOf('day').toISOString()
            );
          }
        }

        if (isAllowSpecialDiscount.allow && !countShowPopupDate && countShowPopup < 4) {
          this.setState({ activeBFCMPopup: true });
          localStorage.setItem(`${tenantApp?.tenantId}-countShowPopupBfcm`, +countShowPopup + 1);
        }
      } catch (error) {
        if (isAllowSpecialDiscount) {
          this.setState({ activeBFCMPopup: true });
        }
      }
    }, 3000);

    try {
      let isFirstTimeAccess = localStorage.getItem(`${tenantApp?.tenantId}-isFirstTimeAccess`);

      if (isFirstTimeAccess) {
        localStorage.removeItem(`${tenantApp?.tenantId}-isFirstTimeAccess`);
      }

      this.setState({
        isFirstTimeAccess
      });
    } catch (error) {}

    this.getGuideConfigFromLocal();

    // Personalization campaign
    const showCampaignBannerType = checkShowPersonalizationCampaignBanner(tenantApp);
    this.setState({ showCampaignBannerType });

    Emitter.on(EVENT_KEY.UPDATE_LOCAL_STORAGE, () => {
      this.getGuideConfigFromLocal();
    });

    this.triggerUpdate();
  }

  triggerUpdate = () => {
    const that = this;
    const targetNode = document.getElementById('BC-Page__FirstColumn');
    const config = { attributes: true, childList: true, subtree: true };
    const callback = function (mutationsList, observer) {
      that.handleSetSticky();
    };
    this.observer = new MutationObserver(callback);
    this.observer.observe(targetNode, config);
  };

  componentWillUnmount() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  componentWillReceiveProps(nextProps) {
    const { personalizationCampaignStatus: currentStatus } = this.props;
    const { personalizationCampaignStatus: nextStatus, tenantApp } = nextProps;
    if (currentStatus !== nextStatus) {
      // Personalization campaign
      const showCampaignBannerType = checkShowPersonalizationCampaignBanner(tenantApp);
      this.setState({ showCampaignBannerType });
    }
  }

  getGuideConfigFromLocal = () => {
    try {
      const guideConfigString = localStorage.getItem(`${getShop()}-guide`);
      const guideConfig = guideConfigString
        ? JSON.parse(guideConfigString)
        : {
            isHideGuideForever: false
          };

      this.setState({
        isHideGuideForever: guideConfig.isHideGuideForever
      });
    } catch (error) {}
  };

  // Render the row based on the number of component in row
  renderRow(row) {
    const oneHalf = row.length === 2 ? true : false;

    return (
      <React.Fragment>
        {row.map(column => {
          const component = renderComponent(column);
          if (!component) return <div key={column}></div>;

          return (
            <Layout.Section oneHalf={oneHalf} key={column}>
              {component}
            </Layout.Section>
          );
        })}
      </React.Fragment>
    );
  }

  // Handle dismiss
  handleDismissBanner = type => {
    try {
      localStorage.setItem(`${getShop()}-dismissMarketingCampaignBanner-dashboard-${type}`, 'true');
      this.setState({ showCampaignBannerType: '' });
    } catch (error) {}
  };

  handleSetSticky = () => {
    const firstCol = document.getElementById('BC-Page__FirstColumn');
    const secondCol = document.getElementById('BC-Page__SecondColumn');
    if (firstCol.offsetHeight > secondCol.offsetHeight) {
      secondCol.style.position = 'sticky';
      secondCol.style.top = '50px';
      firstCol.style.position = 'unset';
      firstCol.style.position = 'unset';
    } else {
      firstCol.style.position = 'sticky';
      firstCol.style.top = '50px';
      secondCol.style.position = 'unset';
      secondCol.style.position = 'unset';
    }
  };

  checkAllowedShowBannerTurbo = () => {
    const { tenantApp } = this.props;
    const { appVersion } = tenantApp;
    const isEnableShopifyIntegration = enableShopifyIntegration(tenantApp);
    const enableAppVersionSwitching = checkUserEnableShowAppVersionSwitching(tenantApp);

    const initAppVersion = appVersion
      ? appVersion
      : isEnableShopifyIntegration
      ? APP_VERSION_TURBO
      : APP_VERSION_OLD_VERSION;

    try {
      const isInWhiteList =
        isInWhiteListTurbo(tenantApp.tenantId) || isInWhiteListTurboV2(tenantApp.tenantId);
      const isAllowedShowBannerTurbo =
        ((!localStorage.getItem(`${getShop()}-${KEY_BOOST_TURBO_BANNER_DASHBOARD}`) &&
          initAppVersion === APP_VERSION_TURBO) ||
          (!localStorage.getItem(`${getShop()}-${KEY_TO_TURBO_BANNER_DASHBOARD}`) &&
            initAppVersion === APP_VERSION_OLD_VERSION)) &&
        isInWhiteList &&
        enableAppVersionSwitching;

      return isAllowedShowBannerTurbo;
    } catch (e) {
      console.log('error', e);
    }
  };

  render() {
    const { tenantApp, enableAISearch, locales, dashboardBlocks, loadingDashboardBlocks } =
      this.props;
    const { showCampaignBannerType } = this.state;
    const selectedPlan = _.get(tenantApp, 'selectedPlan', '');
    const isFreemium = isFreemiumPlan(tenantApp);

    const isAllowedShowBannerTurbo = this.checkAllowedShowBannerTurbo();
    const dashboardBlocksCustom =
      !loadingDashboardBlocks && isAllowedShowBannerTurbo
        ? { ...dashboardBlocks, upgradeTurbo: true }
        : dashboardBlocks;

    // Determine blocks to be rendered
    const displayBlocksTop = getDashboardV4DisplayBlocksColumn(
      DASHBOARD_V4_POSITION.TOP,
      dashboardBlocksCustom,
      { isTrial: !selectedPlan, showCampaignBannerType, isFreemium }
    );
    const displayBlocksFirstColumn = getDashboardV4DisplayBlocksColumn(
      DASHBOARD_V4_POSITION.FIRST,
      dashboardBlocks,
      { isTrial: !selectedPlan, isFreemium }
    );
    const displayBlocksSecondColumn = getDashboardV4DisplayBlocksColumn(
      DASHBOARD_V4_POSITION.SECOND,
      dashboardBlocks,
      { isTrial: !selectedPlan, isFreemium }
    );

    const displayGermanyTestSearchEngineSurvey =
      locales.findIndex(item => item.locale === 'de') > -1 ? true : false;

    const isEnableShopifyIntegration = enableShopifyIntegration(tenantApp);

    return (
      <React.Fragment>
        <MainMenu menuId="menu-home" homepage={true} />
        <div className="BC-AnotherLayoutV3 V2">
          <div className="BC-Page BC-Page__DashboardV3">
            <Page fullWidth>
              <div style={{ width: '100%' }}>
                <Layout>
                  {/* consider build generate function for this layout */}
                  <React.Fragment>
                    {displayBlocksTop.map((layout, layoutIndex) => {
                      if (layout[0] === 'quickStartGuide') {
                        return (
                          <React.Fragment key={`layout-${layoutIndex}`}>
                            {!isEnableShopifyIntegration && (
                              <Layout.Section>
                                {/* Implement installation banner, consider create new file */}
                                <BannerInstallStatus />
                                {/* End of installation banner */}
                              </Layout.Section>
                            )}
                            <Layout.Section>
                              {/* Implement marketing banner, consider create new file */}

                              {renderComponent(layout)}
                            </Layout.Section>
                          </React.Fragment>
                        );
                      }

                      return (
                        <React.Fragment key={`layout-${layoutIndex}`}>
                          <Layout.Section>
                            {/* Implement marketing banner, consider create new file */}

                            {renderComponent(layout, {
                              type: showCampaignBannerType,
                              handleDismissBanner: this.handleDismissBanner
                            })}
                          </Layout.Section>
                        </React.Fragment>
                      );
                    })}
                  </React.Fragment>
                </Layout>
              </div>
            </Page>
            <Page>
              <div className="BC-Page__FirstColumn" id="BC-Page__FirstColumn">
                <Layout>
                  {displayBlocksFirstColumn.map((row, index) => {
                    return (
                      <React.Fragment key={'row-' + index}>{this.renderRow(row)}</React.Fragment>
                    );
                  })}
                </Layout>
              </div>
              <div className="BC-Page__SecondColumn" id="BC-Page__SecondColumn">
                <Layout>
                  {displayBlocksSecondColumn.map((row, index) => {
                    return (
                      <React.Fragment key={'row-' + index}>{this.renderRow(row)}</React.Fragment>
                    );
                  })}
                </Layout>
              </div>
            </Page>
          </div>

          <GermanyTestSearchEngineSurvey type="germanyTestSearchEngine" />

          {/* Lucky draw*/}
          {!displayGermanyTestSearchEngineSurvey && <LuckyDrawModal type="luckyDraw" />}

          {!displayGermanyTestSearchEngineSurvey && <YearEndReviewPopup />}
          {!enableAISearch && <AISearchCampaign0424Popup />}
          <BoostTurboModal tenantId={tenantApp.tenantId} />
        </div>
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state, props) => ({
  dashboardBlocks: _.get(state.dashboardV3, 'dashboardBlocks', {}),
  loadingDashboardBlocks: _.get(state.dashboardV3, 'loading', false),
  notifications: _.get(state.dashboardV3, 'dashboardComponents.notifications.data', []),
  dashboardComponentsLoading: _.get(state.dashboardV3, 'dashboardComponentsLoading', false),
  tenantApp: _.get(state.oauth, 'tenantApp', {}),
  personalizationCampaignStatus: _.get(state.oauth, 'tenantApp.personalizationCampaignStatus', ''),
  language: _.get(state.oauth, 'tenantApp.appLanguage', APP_LANGUAGE_ENGLISH_VALUE),
  steps: _.get(state.quickStartGuide, 'steps', []),
  step: _.get(state.quickStartGuide, 'step', {}),
  enableAISearch: _.get(
    state.oauth.tenantApp,
    'settings.instantSearch.settings.AI-synonyms',
    false
  ),
  locales: _.get(state.translation, 'locales', [])
});

const mapDispatchToProps = (dispatch, ownProps) => ({
  getDetailInvoice: () => dispatch(subscriptionV3Actions.getDetailInvoice(ownProps.i18n)),
  getQuickStartGuides: () => dispatch(quickStartGuideAction.getQuickStartGuides(ownProps.i18n)),
  getDashboardBlocks: () => dispatch(dashboardV3Action.getDashboardBlocks(ownProps.i18n)),
  getCampaignBanners: () => dispatch(dashboardV3Action.getCampaignBanners(ownProps.i18n)),
  getLocales: () => dispatch(TranslationActions.getLocales(ownProps.i18n)),
  getDashboardComponents: language =>
    dispatch(dashboardV3Action.getDashboardComponents(language, ownProps.i18n))
});

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(DashboardV3));
